import {
  GQL_GET_COLLECTOR_COMMENT_TIME,
  GQL_GET_META_INFO,
  GQL_GET_SINGLE_LIKE_INFO,
  GQL_GET_SINGLE_META_INFO,
} from "@/graphql/meta.graphql";
import { MetaInfoContent } from "@/interfaces/meta.interface";
import { currentUserState } from "@/stores/common";
import {
  collectorMetaMergeField,
  collectorMetaState,
  metaMergeField,
  metaState,
} from "@/stores/meta";
import { useLazyQuery, useQuery } from "@apollo/react-hooks";
import { useEffect, useMemo } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

export const useMetaInfos = (fatherIds: string[]) => {
  const setMeta = useSetRecoilState(metaState);
  const currentUser = useRecoilValue(currentUserState);

  const { data, loading, refetch: _refetch } = useQuery<
    { getMetaInfo: MetaInfoContent[] },
    { ids: string[]; userId?: string }
  >(GQL_GET_META_INFO, {
    variables: {
      ids: fatherIds,
      userId: currentUser.id,
    },
    skip: !fatherIds || fatherIds?.length === 0,
  });

  useMemo(() => {
    if (data) {
      const { getMetaInfo } = data;
      setMeta((prev) => ({
        ...prev,
        ...Object.fromEntries(
          getMetaInfo?.map(({ fatherId, ...others }) => [
            fatherId,
            { fatherId, ...others },
          ]) ?? []
        ),
      }));
    }
  }, [data]);

  const refetch = async () => {
    const { data } = await _refetch({ ids: fatherIds });
    if (data) {
      const { getMetaInfo } = data;
      setMeta((prev) => ({
        ...prev,
        ...Object.fromEntries(
          getMetaInfo?.map(({ fatherId, ...others }) => [
            fatherId,
            { fatherId, ...others },
          ]) ?? []
        ),
      }));
    }
  };

  return { loading, refetch };
};

export const useMeta = (fatherId: string, skipComment?: boolean) => {
  const setMeta = useSetRecoilState(metaMergeField(fatherId));
  const currentUser = useRecoilValue(currentUserState);
  const { data, loading, refetch: _refetch } = useQuery<
    { getSingleMetaInfo: MetaInfoContent },
    { id: string; userId?: string; skipComment: boolean }
  >(GQL_GET_SINGLE_META_INFO, {
    variables: {
      id: fatherId,
      userId: currentUser.id,
      skipComment: Boolean(skipComment),
    },
    skip: !fatherId,
  });

  useMemo(() => {
    if (data) {
      setMeta(data?.getSingleMetaInfo);
    }
  }, [data]);

  const refetch = async () => {
    const { data } = await _refetch({
      id: fatherId,
      userId: currentUser.id,
      skipComment: Boolean(skipComment),
    });
    if (data) {
      setMeta(data?.getSingleMetaInfo);
    }
  };

  return { refetch, loading };
};

export const useLazyMetaInfos = (fatherIds: string[]) => {
  const setMeta = useSetRecoilState(metaState);
  const currentUser = useRecoilValue(currentUserState);

  const [_getMeta, { data, loading }] = useLazyQuery<
    { getMetaInfo: MetaInfoContent[] },
    { ids: string[]; userId?: string }
  >(GQL_GET_META_INFO, { fetchPolicy: "cache-and-network", ssr: false });

  const getMeta = () => {
    if (fatherIds?.length > 0) {
      _getMeta({
        variables: { ids: fatherIds, userId: currentUser.id },
      });
    }
  };

  useEffect(() => {
    if (data) {
      const { getMetaInfo } = data;
      setMeta((prev) => ({
        ...prev,
        ...Object.fromEntries(
          getMetaInfo?.map(({ fatherId, ...others }) => [
            fatherId,
            { fatherId, ...others },
          ]) ?? []
        ),
      }));
    }
  }, [data]);

  return { loading, getMeta };
};

export const useLazyMeta = (fatherId: string, skipComment?: boolean) => {
  const setMeta = useSetRecoilState(metaMergeField(fatherId));
  const currentUser = useRecoilValue(currentUserState);
  const [_getMeta, { data, loading }] = useLazyQuery<
    { getSingleMetaInfo: MetaInfoContent },
    { id: string; userId?: string; skipComment: boolean }
  >(GQL_GET_SINGLE_META_INFO, { ssr: false, fetchPolicy: "cache-and-network" });

  const getMeta = () => {
    if (fatherId) {
      _getMeta({
        variables: {
          id: fatherId,
          userId: currentUser.id,
          skipComment: Boolean(skipComment),
        },
      });
    }
  };

  useEffect(() => {
    if (data) {
      setMeta(data?.getSingleMetaInfo);
    }
  }, [data]);

  return { getMeta, loading };
};

export const useMetaLikeAndComment = (fatherId: string) => {
  const meta = useRecoilValue<MetaInfoContent>(metaMergeField(fatherId));

  return useMemo(() => {
    if (meta) {
      return {
        likedStatus: meta.liked?.status ?? false,
        likedTimeCount: meta.likedTime?.count ?? 0,
        myCommentTimeCount: meta.myCommentTime?.count ?? 0,
        commentTimeCount: meta.commentTime?.count ?? 0,
      };
    }
    return {
      likedStatus: false,
      likedTimeCount: 0,
      myCommentTimeCount: 0,
      commentTimeCount: 0,
    };
  }, [meta]);
};

export const useLazyCollectorMetaInfos = (
  fatherIds: string[],
  userId: string
) => {
  const setMeta = useSetRecoilState(collectorMetaState);

  const [_getMeta, { data, loading }] = useLazyQuery<
    { getMetaInfo: MetaInfoContent[] },
    { ids: string[]; userId?: string }
  >(GQL_GET_COLLECTOR_COMMENT_TIME, {
    fetchPolicy: "cache-and-network",
    ssr: false,
  });

  const getMeta = () => {
    if (fatherIds?.length > 0 && userId) {
      _getMeta({
        variables: { ids: fatherIds, userId },
      });
    }
  };

  useEffect(() => {
    if (data) {
      const { getMetaInfo } = data;
      setMeta((prev) => ({
        ...prev,
        ...Object.fromEntries(
          getMetaInfo?.map(({ fatherId, ...others }) => [
            fatherId,
            { fatherId, ...others },
          ]) ?? []
        ),
      }));
    }
  }, [data]);

  return { loading, getMeta };
};

export const useCollectorMetaCountAndLike = (fatherId: string) => {
  const meta = useRecoilValue<MetaInfoContent>(
    collectorMetaMergeField(fatherId)
  );

  return useMemo(() => {
    if (meta) {
      return {
        collectorCommentTime: meta?.collectorCommentTime?.count ?? 0,
      };
    }
    return {
      collectorCommentTime: 0,
    };
  }, [meta]);
};

export const useLazyGetLikeStatus = (fatherId: string) => {
  const currentUser = useRecoilValue(currentUserState);
  const setMeta = useSetRecoilState<MetaInfoContent>(metaMergeField(fatherId));
  const [_getLike, { loading, data }] = useLazyQuery<
    {
      getSingleMetaInfo: MetaInfoContent;
    },
    { fatherId: string; userId: string }
  >(GQL_GET_SINGLE_LIKE_INFO, { ssr: false, fetchPolicy: "cache-and-network" });
  const getLike = () => {
    if (currentUser.id && fatherId) {
      _getLike({ variables: { fatherId, userId: currentUser.id } });
    }
  };
  useEffect(() => {
    if (data) {
      setMeta(data?.getSingleMetaInfo);
    }
  }, [data]);
  return { loading, getLike };
};
